import axios from "axios";
import { Dispatch } from "redux";
import { ActionTypes } from "./types";
import { API_URL } from "../utils/api";
import { errorToText } from "../utils/errors";
import { APP_TOKEN_NAME, setAxiosToken } from "../utils/AxiosToken";

/**
 * * ****************************** INTERFACES *****************************
 */

export interface API_GetUsersDetails {
  user_id: string;
  user_type_id: number; // Admin: 1, User: 2
  fname: string;
  lname: string;
  phone_number: string;
  email: string;
  username: string;
  date_time: string;
}

export interface API_UserLoginDetailsData {
  message: string;
  data: API_GetUsersDetails[];
  token: string;
  resultCount: number;
}

export interface Auth {
  loading: boolean;
  isAuthenticated: boolean;
  token: string;
  user: API_GetUsersDetails | null;
}

//* ********************** ACTION TYPE INTERCACES ********************** */
export interface FerchLoginDetails {
  type: ActionTypes.LOGIN_DETAILS;
  payload: Auth;
}

export interface GetTwoFactorAuth {
  type: ActionTypes.SET_TWO_FACTOR_AUTH;
  payload: boolean;
}

export interface LoginSuccessDetails {
  type: ActionTypes.USER_LOGIN_SUCCESS_DATA;
  payload: API_UserLoginDetailsData;
}

export interface CleanUserDetails {
  type: ActionTypes.CLEAN_USER_DETAILS;
}

export interface LogoutUser {
  type: ActionTypes.LOGOUT;
}

export interface FerchLoginDetails {
  type: ActionTypes.LOGIN_DETAILS;
  payload: Auth;
}

export interface UpdateProfileDetails {
  type: ActionTypes.UPDATE_PROFILE_DETAILS;
  payload: {
    email: string;
    phone_number: string;
    residence: {
      province_id: string;
      province_name: string;
      district_id: string;
      district_name: string;
      sector_id: string;
      sector_name: string;
    };
  };
}
/**
 * * ****************************** ACTIONS *****************************
 */

export const FC_CleanUserDetails = () => {
  return (dispatch: Dispatch) => {
    dispatch<CleanUserDetails>({
      type: ActionTypes.CLEAN_USER_DETAILS,
    });
  };
};

/**
 * @description Register the account to the api
 * @param account
 * @param MsgHandler return the error from the API
 * @returns
 */
export const FC_Login = (
  data: {
    username: string;
    password: string;
  },
  CallbackFunc: Function
) => {
  return async (dispatch: Dispatch) => {
    try {
      const res = await axios.post<API_UserLoginDetailsData>(
        `${API_URL}/users/auth/login`,
        data
      );

      console.log({ data_after_login: res.data });

      localStorage.setItem(APP_TOKEN_NAME, res.data.token);
      dispatch<LoginSuccessDetails>({
        type: ActionTypes.USER_LOGIN_SUCCESS_DATA,
        payload: res.data,
      });
      CallbackFunc(true, "");
    } catch (error: any) {
      console.log("Login err: ", { ...error });
      // CallbackFunc(false, errorToText(error));
      CallbackFunc(false, "Invalid username or password!");
    }
  };
};

/**
 * @description Check if the user is logged in based on the logged in account
 * @param account
 * @param MsgHandler return the error from the API
 * @returns
 */

export const FC_CheckLoggedIn = () => {
  return async (dispatch: Dispatch) => {
    if (localStorage.getItem(APP_TOKEN_NAME) === null) {
      dispatch<LogoutUser>({
        type: ActionTypes.LOGOUT,
      });
      return false;
    }
    try {
      setAxiosToken();
      const res = await axios.get<API_GetUsersDetails>(
        `${API_URL}/users/auth/currentuser`
      );
      dispatch<LoginSuccessDetails>({
        type: ActionTypes.USER_LOGIN_SUCCESS_DATA,
        payload: {
          message: "",
          token: localStorage.getItem(APP_TOKEN_NAME)!,
          data: [res.data],
          resultCount: 1,
        },
      });
    } catch (error: any) {
      console.log("User not: ", { ...error });
      dispatch<LogoutUser>({
        type: ActionTypes.LOGOUT,
      });
    }
  };
};

/**
 * @description Logout the user into the system
 * @returns null
 */
export const FC_Logout = () => {
  return (dispatch: Dispatch) => {
    dispatch<LogoutUser>({
      type: ActionTypes.LOGOUT,
    });
  };
};

/**
 * @description Register the account to the api
 * @param account
 * @param MsgHandler return the error from the API
 * @returns
 */

export interface FC_UpdateProfile_Interface {
  user_id: string;
  phone_number: string;
  email: string;
  residence: {
    province_id: string;
    province_name: string;
    district_id: string;
    district_name: string;
    sector_id: string;
    sector_name: string;
  };
}

export interface FC_UpdateResidence_Interface {
  province_id: string;
  province_name: string;
  district_id: string;
  district_name: string;
  sector_id: string;
  sector_name: string;
}
/**
 * Update user email and phone number
 * @param data
 * @param callback
 * @returns
 */
export const FC_UpdateProfile = (
  data: FC_UpdateProfile_Interface,
  callback: Function
) => {
  return async (dispatch: Dispatch) => {
    try {
      setAxiosToken();

      await axios.patch(`${API_URL}/users/updateinfo`, {
        user_id: data.user_id,
        phone_number: data.phone_number,
        email: data.email,
        residence: data.residence,
      });

      dispatch<UpdateProfileDetails>({
        type: ActionTypes.UPDATE_PROFILE_DETAILS,
        payload: data,
      });

      callback(true, "");
    } catch (error) {
      callback(false, errorToText(error));
    }
  };
};

export interface FC_ChangePassword_Interface {
  user_id: string;
  old_password: string;
  new_password: string;
}

export const FC_ChangePassword = (
  data: FC_ChangePassword_Interface,
  callback: Function
) => {
  return async (dispatch: Dispatch) => {
    try {
      setAxiosToken();

      await axios.patch(`${API_URL}/users/changepassword`, {
        user_id: data.user_id,
        old_password: data.old_password,
        new_password: data.new_password,
      });

      callback(true, "");
    } catch (error) {
      callback(false, errorToText(error));
    }
  };
};

/**
 * Edit users documents
 * @param data
 * @param user_id
 * @param callback
 * @returns
 */

export interface FC_ForgetPassword_Interface {
  address: string;
  verify_type: string;
}
/**
 * Send the reset password
 * @param data
 * @param callback
 * @returns
 */
export const FC_ForgetPassword = (
  data: FC_ForgetPassword_Interface,
  callback: Function
) => {
  return async (dispatch: Dispatch) => {
    try {
      setAxiosToken();

      const res = await axios.post<{
        message: string;
        code?: string;
      }>(`${API_URL}/users/password/address`, {
        address: data.address,
        verify_type: data.verify_type,
      });

      console.log({ CODE: res.data.code });

      callback(true, res.data.message);
    } catch (error) {
      callback(false, errorToText(error));
    }
  };
};

export interface FC_ForgetPassword_Check_Interface {
  address: string;
  verification_code: string;
  new_password: string;
}
/**
 * Send the new password and update that
 * @param data
 * @param callback
 * @returns
 */
export const FC_ForgetPassword_Check = (
  data: FC_ForgetPassword_Check_Interface,
  callback: Function
) => {
  return async (dispatch: Dispatch) => {
    try {
      setAxiosToken();

      const res = await axios.post<{
        message: string;
      }>(`${API_URL}/users/password/reset`, {
        address: data.address,
        verification_code: data.verification_code,
        new_password: data.new_password,
      });

      callback(true, res.data.message);
    } catch (error) {
      callback(false, errorToText(error));
    }
  };
};
